import { DatePipe, DecimalPipe, LocationStrategy, HashLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MsalModule, MsalGuard } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { provideCommonRbacEvaluator } from '@cdcn/common-rbac-evaluator-lib';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { GridModule, ExcelModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { SortableModule } from '@progress/kendo-angular-sortable';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { provideUserIdleConfig } from 'angular-user-idle';
import { ToastrModule } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { APP_ROUTES } from './app.routes';
import { AppConstants } from './core/appConstant';
import { RedirectNavigation } from './core/config/redirect-navigation';
import { AccessDeniedInterceptor } from './core/interceptors/AccessDeniedInterceptor';
import { CustomHttpInterceptorService } from './core/interceptors/CustomHttpInterceptorService';
import { TokenInterceptor } from './core/interceptors/tokenInterceptor';
import { AccessRightsRouteGurd } from './core/routegurds/accessrightsroutegurd';
import { AppInsightsService } from './core/services/app-insights.service';
import { AUTHORIZATION_API_BASE_URL } from './core/services/authorizationapi/authorizationapi.services';
import { BILLING_API_BASE_URL } from './core/services/billingapi/billingfunctionapi.services';
import { CONFIGURATION_API_BASE_URL } from './core/services/configurationapi/configurationfunctionapi.services';
import { DateConverterService } from './core/services/dateconverter.service';
import { ErrorHandlerService } from './core/services/error-handler.service';
import {
    EVENT_DRIVEN_PROCESS_TRACKING_API_BASE_URL
} from './core/services/eventdrivenprocesstracking/eventdrivenprocesstrackingapi.services';
import { FALLOUT_API_BASE_URL } from './core/services/falloutapi/falloutfunctionapi.services';
import { HierarchicalPermissionsService } from './core/services/hierarchical-permissions.service';
import { IAM_API_BASE_URL } from './core/services/iamapi/iamapi.services';
import { IAM_AUTHR_API_BASE_URL } from './core/services/iamauthrapi/iamauthrapi.services';
import { INTFRAMEWORK_API_BASE_URL } from './core/services/intframeworkapi/intframeworkapi.services';
import { NOTE_API_BASE_URL } from './core/services/noteapi/notefunctionapi.services';
import { NOTIFICATION_API_BASE_URL } from './core/services/notificationsapi/notificationfunctionapi.services';
import { PAYROLL_API_BASE_URL } from './core/services/payrollapi/payrollfunctionapi.services';
import { PEOPLE_API_BASE_URL } from './core/services/peopleapi/peoplefunctionapi.services';
import { SHIFT_API_BASE_URL } from './core/services/shiftapi/shiftapi.services';
import { SHIFTENTRY_API_BASE_URL } from './core/services/shiftentryapi/shiftentryapi.services';
import { API_BASE_URL, PortalUserService } from './core/services/u2api.services';
import { UTEX_API_BASE_URL } from './core/services/utexapi/utexapi.services';
import { CDCNCompanyService } from './main-header/cdcn-company-service/cdcn-company.service';
import { CommonDropdownService } from './shared/services/common-dropdown.service';
import { DatePickerCommonService } from './shared/services/date-picker-common.service';
import { CommonFunctions } from './shared/utilities/common-functions';
import { MaintenanceEffects } from './u2-maintenance/maintenance-state/maintenance.effects';
import { maintenanceReducer } from './u2-maintenance/maintenance-state/maintenance.reducer';
import { PersonEffects } from './u2-person/person-state/person.effects';
import { personReducer } from './u2-person/person-state/person.reducer';
import { IconsModule } from '@progress/kendo-angular-icons';
import { provideRouter } from '@angular/router';

const scopesToConsent = environment.apiScope;

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      MsalModule.forRoot(
        new PublicClientApplication({
          auth: {
            authority: 'https://login.microsoftonline.com/' + environment.tenant,
            clientId: environment.clientId,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: '/',
            onRedirectNavigate: RedirectNavigation.onRedirectNavigate,
          },
          cache: {
            cacheLocation: environment.cacheLocation,
          },
        }),
        {
          interactionType: InteractionType.Redirect, // MSAL Guard Configuration
          authRequest: {
            extraScopesToConsent: [scopesToConsent],
            prompt: 'select_account',
          },
        },
        {
          interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
          protectedResourceMap: new Map([]),
        },
      ),
      StoreModule.forRoot({}),
      EffectsModule.forRoot([]),
      StoreModule.forFeature('persons', personReducer),
      EffectsModule.forFeature([PersonEffects]),
      StoreModule.forFeature('maintenance', maintenanceReducer),
      EffectsModule.forFeature([MaintenanceEffects]),
      StoreDevtoolsModule.instrument({
        name: 'CDMS Devtools',
        maxAge: 25,
        logOnly: environment.production as any,
        connectInZone: true,
      }),
      SortableModule,
      GridModule,
      ExcelModule,
      SortableModule,
      InputsModule,
      FormsModule,
      ToastrModule.forRoot({
        timeOut: 3000,
        preventDuplicates: true,
        enableHtml: true,
      }),
      ReactiveFormsModule,
      DialogModule,
      TooltipModule,
      DropDownsModule,
      LayoutModule,
      IconsModule,
    ),
    provideCommonRbacEvaluator({
      unauthorizedRoute: AppConstants.UnauthorizedRoute,
      showPermissionHint: false,
    }),
    DatePipe,
    DecimalPipe,
    CommonFunctions,
    MsalGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    AccessRightsRouteGurd,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccessDeniedInterceptor,
      multi: true,
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: API_BASE_URL,
      useFactory: () => environment.apiHost,
    },
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptorService, multi: true },
    PortalUserService,
    DateConverterService,
    AppInsightsService,
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    DatePickerCommonService,
    CommonDropdownService,
    CDCNCompanyService,
    {
      provide: BILLING_API_BASE_URL,
      useFactory: () => environment.billingApiHost,
    },
    {
      provide: NOTIFICATION_API_BASE_URL,
      useFactory: () => environment.notificationApiHost,
    },
    {
      provide: PAYROLL_API_BASE_URL,
      useFactory: () => environment.payrollApiHost,
    },
    {
      provide: PEOPLE_API_BASE_URL,
      useFactory: () => environment.peopleApiHost,
    },
    {
      provide: IAM_API_BASE_URL,
      useFactory: () => environment.iamApiHost,
    },
    {
      provide: IAM_AUTHR_API_BASE_URL,
      useFactory: () => environment.iamAuthrApiHost,
    },
    {
      provide: UTEX_API_BASE_URL,
      useFactory: () => environment.utexApiHost,
    },
    {
      provide: AUTHORIZATION_API_BASE_URL,
      useFactory: () => environment.authorizationApiHost,
    },
    {
      provide: CONFIGURATION_API_BASE_URL,
      useFactory: () => environment.configurationApiHost,
    },
    {
      provide: FALLOUT_API_BASE_URL,
      useFactory: () => environment.falloutApiHost,
    },
    {
      provide: NOTE_API_BASE_URL,
      useFactory: () => environment.noteApiHost,
    },
    {
      provide: UTEX_API_BASE_URL,
      useFactory: () => environment.utexApiHost,
    },
    {
      provide: SHIFT_API_BASE_URL,
      useFactory: () => environment.shiftApiHost,
    },
    {
      provide: SHIFTENTRY_API_BASE_URL,
      useFactory: () => environment.shiftentryApiHost,
    },
    {
      provide: EVENT_DRIVEN_PROCESS_TRACKING_API_BASE_URL,
      useFactory: () => environment.eventdrivenprocesstrackingApiHost,
    },
    {
      provide: INTFRAMEWORK_API_BASE_URL,
      useFactory: () => environment.intframeworkApiHost,
    },
    HierarchicalPermissionsService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideUserIdleConfig({
      idle: AppConstants.sessionTimeOut,
      timeout: AppConstants.sessionLogOut,
      ping: AppConstants.keepAliveIntervel,
    }),
    provideRouter(APP_ROUTES),
  ],
};
